import { symbolOfAttributeHasBeenSet, symbolOfMarkAttributeHasBeenSet, symbolOfStopAttributeChangedCallback } from "./internal/symbols.js";
const defaultConverter = {
  toAttribute(value, type) {
    switch (type) {
      case Boolean:
        return value ? "" : null;
    }
    return value;
  },
  fromAttribute(value, type) {
    switch (type) {
      case Boolean:
        return value !== null && value !== "false";
      case Number:
        return value === null ? null : Number(value);
    }
    return value;
  }
};

/**
 * Change function that returns true if `value` is different from `oldValue`.
 * This method is used as the default for a property's `hasChanged` function.
 */
const notEqual = (value, old) => {
  // This ensures (old==NaN, value==NaN) always returns false
  return old !== value && (old === old || value === value);
};
const defaultPropertyDeclaration = {
  attribute: true,
  type: String,
  converter: defaultConverter,
  // reflect: true,
  render: true,
  hasChanged: notEqual
};

// Note: `prefix` is a native prop on Element, but it's only used in XML documents.
const allowedNativeProps = new Set(["prefix"]);
export function createDecorators() {
  const attributeReflections = new Map();
  const definedProperties = new Set();
  const definedMethods = new Set();
  const definedEvents = new Set();
  function defineElement(name) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return (value, _ref) => {
      let {
        kind,
        name: className,
        addInitializer
      } = _ref;
      // istanbul ignore next
      if (process.env.NODE_ENV === "development" && kind !== "class") {
        throw new Error(`Invalid usage of \`@defineElement()\` on a ${kind}: "${className}"`);
      }
      addInitializer(function () {
        var _superClass$observedA, _superClass$__attribu, _superClass$_dev_only, _superClass$_dev_only2, _superClass$_dev_only3;
        const superClass = Object.getPrototypeOf(this);
        const mergedAttributes = mergeIterables((_superClass$observedA = superClass.observedAttributes) !== null && _superClass$observedA !== void 0 ? _superClass$observedA : [], attributeReflections.keys());
        defineReadonlyProperty(this, "observedAttributes", mergedAttributes);
        const mergedAttributeReflections = new Map([...((_superClass$__attribu = superClass.__attributeReflections) !== null && _superClass$__attribu !== void 0 ? _superClass$__attribu : []), ...attributeReflections]);
        defineReadonlyProperty(this, "__attributeReflections", mergedAttributeReflections);
        defineReadonlyProperty(this, "styleTexts", options.styleTexts);
        defineReadonlyProperty(this, "shadowOptions", options.shadowOptions !== false ? {
          mode: "open",
          ...options.shadowOptions
        } : null);
        defineReadonlyProperty(this, "__tagName", name);
        const mergedProperties = mergeIterables((_superClass$_dev_only = superClass._dev_only_definedProperties) !== null && _superClass$_dev_only !== void 0 ? _superClass$_dev_only : [], definedProperties);
        defineReadonlyProperty(this, "_dev_only_definedProperties", mergedProperties);
        const mergedMethods = mergeIterables((_superClass$_dev_only2 = superClass._dev_only_definedMethods) !== null && _superClass$_dev_only2 !== void 0 ? _superClass$_dev_only2 : [], definedMethods);
        defineReadonlyProperty(this, "_dev_only_definedMethods", mergedMethods);
        const mergedEvents = mergeIterables((_superClass$_dev_only3 = superClass._dev_only_definedEvents) !== null && _superClass$_dev_only3 !== void 0 ? _superClass$_dev_only3 : [], definedEvents);
        defineReadonlyProperty(this, "_dev_only_definedEvents", mergedEvents);
        customElements.define(name, this);
        if (Array.isArray(options.alias)) {
          for (const alias of options.alias) {
            // Use a parenthesized expression to make the class has no name.
            const Alias = (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            0, class extends value {});
            defineReadonlyProperty(Alias, "__tagName", alias);
            customElements.define(alias, Alias);
          }
        }
      });
    };
  }
  function property(options) {
    return function (_ref2, _ref3) {
      let {
        get,
        set
      } = _ref2;
      let {
        kind,
        name,
        static: isStatic,
        private: isPrivate
      } = _ref3;
      // istanbul ignore next
      if (process.env.NODE_ENV !== "production") {
        if (kind !== "accessor") {
          throw new Error(`Invalid usage of \`@property()\` on a ${kind}: "${String(name)}"`);
        }
        if (typeof name !== "string") {
          throw new Error(`Invalid usage of \`@property()\` on a ${kind} of ${typeof name}`);
        }
        if (isStatic) {
          throw new Error(`Invalid usage of \`@property()\` on a static ${kind}: "${name}"`);
        }
        if (isPrivate) {
          throw new Error(`Invalid usage of \`@property()\` on a private ${kind}: "${name}"`);
        }
        if (name in HTMLElement.prototype && !allowedNativeProps.has(name)) {
          const message = `"${name}" is a native HTMLElement property, and is deprecated to be used as a brick property.`;
          throw new Error(message);
        }
      }
      definedProperties.add(name);
      const _options = Object.assign({}, defaultPropertyDeclaration, options);
      const attrName = attributeNameForProperty(name, _options);
      if (attrName !== undefined) {
        attributeReflections.set(attrName, {
          ..._options,
          property: name
        });
      }
      return {
        get() {
          // If the attribute has been set (not by initialization), returns the
          // value converted by the attribute. Otherwise returns the prop value.
          // This works as a type conversion when reading a prop value.
          if (attrName !== undefined && this[symbolOfAttributeHasBeenSet](attrName)) {
            return _options.converter.fromAttribute(this.getAttribute(attrName), _options.type);
          }
          return get.call(this);
        },
        set(value) {
          const oldValue = get.call(this);
          set.call(this, value);
          if (_options.hasChanged(value, oldValue)) {
            if (attrName !== undefined) {
              const attrValue = _options.converter.toAttribute(value, _options.type);
              this[symbolOfStopAttributeChangedCallback](true);
              if (attrValue == null) {
                this.removeAttribute(attrName);
              } else {
                this.setAttribute(attrName, attrValue);
              }
              this[symbolOfMarkAttributeHasBeenSet](attrName);
              this[symbolOfStopAttributeChangedCallback](false);
            }
            if (_options.render) {
              this._requestRender();
            }
          }
        },
        init(initialValue) {
          if (attrName !== undefined && _options.hasChanged(initialValue, undefined)) {
            const attrValue = _options.converter.toAttribute(initialValue, _options.type);
            if (attrValue != null) {
              // No attributes should be created during constructing custom elements.
              // This is true even if the work is done inside a constructor-initiated microtask.
              // https://html.spec.whatwg.org/multipage/custom-elements.html#custom-element-conformance
              requestAnimationFrame(() => {
                if (!this[symbolOfAttributeHasBeenSet](attrName)) {
                  this[symbolOfStopAttributeChangedCallback](true);
                  this.setAttribute(attrName, attrValue);
                  this[symbolOfStopAttributeChangedCallback](false);
                }
              });
            }
          }
          return initialValue;
        }
      };
    };
  }
  function method(options) {
    return function (_value, _ref4) {
      let {
        kind,
        name,
        static: isStatic,
        private: isPrivate,
        addInitializer
      } = _ref4;
      // istanbul ignore next
      if (process.env.NODE_ENV !== "production") {
        if (kind !== "method") {
          throw new Error(`Invalid usage of \`@method()\` on a ${kind}: "${String(name)}"`);
        }
        if (typeof name !== "string") {
          throw new Error(`Invalid usage of \`@method()\` on a ${kind} of ${typeof name}`);
        }
        if (isStatic) {
          throw new Error(`Invalid usage of \`@method()\` on a static ${kind}: "${name}"`);
        }
        if (isPrivate) {
          throw new Error(`Invalid usage of \`@method()\` on a private ${kind}: "${name}"`);
        }
        if (name in HTMLElement.prototype && !allowedNativeProps.has(name)) {
          const message = `"${name}" is a native HTMLElement property, and is deprecated to be used as a brick method.`;
          throw new Error(message);
        }
      }
      if (options !== null && options !== void 0 && options.bound) {
        addInitializer(function () {
          this[name] = this[name].bind(this);
        });
      }
      definedMethods.add(name);
    };
  }
  function event(options) {
    return function (_ref5, _ref6) {
      let {
        get
      } = _ref5;
      let {
        kind,
        name,
        static: isStatic,
        private: isPrivate
      } = _ref6;
      // istanbul ignore next
      if (process.env.NODE_ENV !== "production") {
        if (kind !== "accessor") {
          throw new Error(`Invalid usage of \`@event()\` on a ${kind}: "${String(name)}"`);
        }
        if (!isPrivate) {
          throw new Error(`Invalid usage of \`@event()\` on a non-private ${kind}: "${String(name)}"`);
        }
        if (isStatic) {
          throw new Error(`Invalid usage of \`@event()\` on a static ${kind}: "${String(name)}"`);
        }
      }
      const {
        type,
        ...eventInit
      } = options;
      definedEvents.add(type);
      return {
        get() {
          return get.call(this);
        },
        set() {
          throw new Error("Decorated events are readonly");
        },
        init(initialValue) {
          // istanbul ignore next
          if (process.env.NODE_ENV === "development" && initialValue !== undefined) {
            throw new Error(`Do not set an initial value for a decorated event: "${String(name)}"`);
          }
          return Object.freeze({
            emit: detail => this.dispatchEvent(new CustomEvent(type, {
              ...eventInit,
              detail
            }))
          });
        }
      };
    };
  }
  return {
    defineElement,
    property,
    method,
    event
  };
}
function defineReadonlyProperty(target, propName, propValue) {
  Object.defineProperty(target, propName, {
    get() {
      return propValue;
    },
    configurable: true
  });
}
function mergeIterables(list1, list2) {
  const newList = new Set(list1);
  for (const item of list2) {
    newList.add(item);
  }
  return [...newList];
}
function attributeNameForProperty(name, options) {
  const attribute = options.attribute;
  return attribute === false ? undefined : typeof attribute === "string" ? attribute : name.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`);
}
//# sourceMappingURL=createDecorators.js.map